// src/modules/communications/pages/CommunicationsDashboard.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FaPhone, 
  FaSms, 
  FaUser, 
  FaCalendar, 
  FaChartLine, 
  FaSpinner,
  FaArrowRight,
  FaArrowUp,
  FaArrowDown,
  FaHistory,
  FaExclamationTriangle,
  FaQuestionCircle,
  FaPhoneSlash,
  FaPhoneVolume,
  FaChevronDown,
  FaChevronUp,
  FaReply,
  FaPlus,
  FaVolumeUp
} from 'react-icons/fa';
import { getCommunicationsStats } from '../services/communicationService';
import DashboardLayout from '../../../layouts/DashboardLayout';
import { formatDateTime, formatPhoneNumber, formatDuration } from '../utils/formatUtils';
import { useCommunication } from '../context/CommunicationContext';
import { useNavigate } from 'react-router-dom';
import TranscriptionSection from '../../../components/communications/TranscriptionSection';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Styled Components
const DashboardContainer = styled.div`
  padding: ${props => props.theme.spacing(2)};
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const StatCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${props => props.theme.spacing(3)};
  margin-bottom: ${props => props.theme.spacing(4)};
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(2)};
  
  svg {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-right: ${props => props.theme.spacing(1)};
  }
  
  h3 {
    margin: 0;
    font-size: ${props => props.theme.typography.fontSize.medium};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const StatDetail = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  
  .detail-label {
    display: flex;
    align-items: center;
    
    svg {
      margin-right: ${props => props.theme.spacing(0.5)};
      font-size: 0.8rem;
    }
  }
`;

const DashboardSection = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(3)};
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(3)};
  
  h2 {
    margin: 0;
    font-size: ${props => props.theme.typography.fontSize.large};
    color: ${props => props.theme.colors.primary};
    display: flex;
    align-items: center;
    
    svg {
      margin-right: ${props => props.theme.spacing(1)};
    }
  }
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${props => props.theme.spacing(3)};
  margin-bottom: ${props => props.theme.spacing(4)};
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ChartContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 350px;
`;

const ChartTitle = styled.h3`
  margin-top: 0;
  margin-bottom: ${props => props.theme.spacing(2)};
  font-size: ${props => props.theme.typography.fontSize.medium};
  color: ${props => props.theme.colors.primary};
`;

const RecentActivityContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing(4)};
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

const ActivityItem = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ActivityItemHeader = styled.div`
  display: flex;
  padding: ${props => props.theme.spacing(2)};
  gap: ${props => props.theme.spacing(2)};
  cursor: pointer;
  align-items: center;

  &:hover {
    background: ${props => props.theme.colors.background};
  }
`;

const ActivityExpandedDetails = styled.div`
  padding: ${props => props.theme.spacing(2)};
  padding-top: 0;
  border-top: 1px solid ${props => props.theme.colors.inputGray}22;
  background: ${props => props.theme.colors.background};
`;

const ActivityDetailRow = styled.div`
  margin-bottom: ${props => props.theme.spacing(2)};

  label {
    display: block;
    font-size: ${props => props.theme.typography.fontSize.small};
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: ${props => props.theme.spacing(0.5)};
  }
`;

const ActivitySmsBody = styled.div`
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.borderRadius};
  margin-top: ${props => props.theme.spacing(1)};
  white-space: pre-wrap;
  font-size: ${props => props.theme.typography.fontSize.small};
`;

const ActivityActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(2)};
`;

const ActivityActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.small};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primary}22;
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const ExpandIcon = styled.div`
  flex-shrink: 0;
  margin-left: auto;

  svg {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => {
    if (props.type === 'call') {
      return props.direction === 'inbound' ? '#27ae60' : '#3498db';
    } else {
      return props.direction === 'inbound' ? '#e67e22' : '#9b59b6';
    }
  }};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    font-size: 1.2rem;
  }
`;

const ActivityContent = styled.div`
  flex-grow: 1;
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing(1)};
  
  .activity-type {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(1)};
    color: ${props => props.theme.colors.textPrimary};
  }
  
  .activity-time {
    font-size: ${props => props.theme.typography.fontSize.small};
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const ActivityDetail = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
  
  .message-preview {
    margin-top: ${props => props.theme.spacing(0.5)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 500px;
  }
`;

const ActivityLead = styled.div`
  margin-top: ${props => props.theme.spacing(1)};
  
  a {
    display: inline-flex;
    align-items: center;
    gap: ${props => props.theme.spacing(0.5)};
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.typography.fontSize.small};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .no-lead {
    font-style: italic;
    color: ${props => props.theme.colors.textSecondary};
    font-size: ${props => props.theme.typography.fontSize.small};
  }
  
  .placeholder-badge {
    font-size: 0.7rem;
    padding: 2px 6px;
    border-radius: 10px;
    background: ${props => props.theme.colors.warning}33;
    color: ${props => props.theme.colors.warning};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ViewAllLink = styled.a`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-size: ${props => props.theme.typography.fontSize.small};
  
  svg {
    margin-left: ${props => props.theme.spacing(0.5)};
  }
  
  &:hover {
    text-decoration: underline;
  }
`;

const PlaceholderLeadsContainer = styled.div`
  margin-top: ${props => props.theme.spacing(4)};
`;

const PlaceholderLeadsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing(2)};
`;

const PlaceholderLeadCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const PlaceholderLeadHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(1)};
  
  h4 {
    margin: 0;
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(1)};
  }
  
  .placeholder-badge {
    padding: 2px 8px;
    border-radius: 10px;
    background: ${props => props.theme.colors.warning}33;
    color: ${props => props.theme.colors.warning};
    font-size: ${props => props.theme.typography.fontSize.small};
    font-weight: normal;
  }
`;

const PlaceholderLeadPhone = styled.div`
  font-size: ${props => props.theme.typography.fontSize.medium};
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const PlaceholderLeadStats = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  
  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const PlaceholderLeadDate = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
`;

// Dashboard component
const CommunicationsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const { openCallModal, openSmsComposer } = useCommunication();
  const navigate = useNavigate();

  const URL_API = process.env.REACT_APP_API_PUBLIC || "https://api.mrpatchasphalt.com";

  const isAudioUrl = (url) => {
    const ext = url.split('.').pop().split('?')[0].toLowerCase();
    return ['amr', 'ogg', 'mp3', 'wav', 'm4a', '3gp', 'aac', 'mp4'].includes(ext);
  };

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const data = await getCommunicationsStats();
        setStats(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching communication stats:', err);
        setError('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
    
    // Refresh stats periodically
    const intervalId = setInterval(fetchStats, 60000); // every minute
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Prepare pie chart data
  const getPieData = () => {
    if (!stats) return [];
    
    return [
      { name: 'Inbound', value: stats.calls.inbound + stats.sms.inbound },
      { name: 'Outbound', value: stats.calls.outbound + stats.sms.outbound }
    ];
  };
  
  // Combine chart data for calls and SMS
  const combineChartData = () => {
    if (!stats) return [];
    
    const dateMap = new Map();
    
    // Process call data
    stats.charts.daily_calls.forEach(item => {
      dateMap.set(item.date, { date: item.date, calls: parseInt(item.count), sms: 0 });
    });
    
    // Process SMS data
    stats.charts.daily_sms.forEach(item => {
      if (dateMap.has(item.date)) {
        const existing = dateMap.get(item.date);
        existing.sms = parseInt(item.count);
      } else {
        dateMap.set(item.date, { date: item.date, calls: 0, sms: parseInt(item.count) });
      }
    });
    
    // Convert map to array and sort by date
    return Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
  };
  
  // Handle placeholder lead click
  const handlePlaceholderLeadClick = (leadId) => {
    navigate(`/communications/placeholder-leads/${leadId}`);
  };
  
  // Toggle expand/collapse for activity items
  const toggleItemExpansion = (id) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Handle lead click without toggling expansion
  const handleLeadClick = (e, leadId) => {
    e.stopPropagation();
    navigate(`/leads/view/${leadId}`);
  };
  
  if (loading && !stats) {
    return (
      <DashboardLayout>
        <LoadingContainer>
          <FaSpinner />
        </LoadingContainer>
      </DashboardLayout>
    );
  }
  
  // Even if we're refreshing, we can still show the existing stats
  const chartData = stats ? combineChartData() : [];
  const pieData = stats ? getPieData() : [];
  
  return (
    <DashboardLayout>
      <DashboardContainer>
        <DashboardHeader>
          <Title>Phone Dashboard</Title>
        </DashboardHeader>
        
        {/* Summary Stats Cards */}
        <StatCardsContainer>
          {/* Total Communications Card */}
          <StatCard>
            <StatHeader>
              <FaPhone />
              <h3>Total Communications</h3>
            </StatHeader>
            <StatValue>{stats?.summary.total_communications || 0}</StatValue>
            <StatDetail>
              <span className="detail-label">
                <FaPhone /> Calls
              </span>
              <span>{stats?.calls.total || 0}</span>
            </StatDetail>
            <StatDetail>
              <span className="detail-label">
                <FaSms /> SMS
              </span>
              <span>{stats?.sms.total || 0}</span>
            </StatDetail>
          </StatCard>
          
          {/* Today's Activity Card */}
          <StatCard>
            <StatHeader>
              <FaCalendar />
              <h3>Today's Activity</h3>
            </StatHeader>
            <StatValue>{(stats?.calls.past_24h || 0) + (stats?.sms.past_24h || 0)}</StatValue>
            <StatDetail>
              <span className="detail-label">
                <FaPhone /> Calls Today
              </span>
              <span>{stats?.calls.past_24h || 0}</span>
            </StatDetail>
            <StatDetail>
              <span className="detail-label">
                <FaSms /> SMS Today
              </span>
              <span>{stats?.sms.past_24h || 0}</span>
            </StatDetail>
          </StatCard>
          
          {/* Unknown Contacts Card */}
          <StatCard>
            <StatHeader>
              <FaQuestionCircle />
              <h3>Unknown Contacts</h3>
            </StatHeader>
            <StatValue>{stats?.placeholder_leads.count || 0}</StatValue>
            <StatDetail>
              <span className="detail-label">
                <FaPhone /> With Calls
              </span>
              <span>{stats?.calls.with_placeholder_leads || 0}</span>
            </StatDetail>
            <StatDetail>
              <span className="detail-label">
                <FaSms /> With SMS
              </span>
              <span>{stats?.sms.with_placeholder_leads || 0}</span>
            </StatDetail>
          </StatCard>
          
          {/* Direction Stats Card */}
          <StatCard>
            <StatHeader>
              <FaExclamationTriangle />
              <h3>Direction</h3>
            </StatHeader>
            <StatValue>
              {(stats?.calls.inbound || 0) + (stats?.sms.inbound || 0)}
              <span style={{ fontSize: '1rem', marginLeft: '8px' }}>inbound</span>
            </StatValue>
            <StatDetail>
              <span className="detail-label">
                <FaArrowDown /> Inbound
              </span>
              <span>{(stats?.calls.inbound || 0) + (stats?.sms.inbound || 0)}</span>
            </StatDetail>
            <StatDetail>
              <span className="detail-label">
                <FaArrowUp /> Outbound
              </span>
              <span>{(stats?.calls.outbound || 0) + (stats?.sms.outbound || 0)}</span>
            </StatDetail>
          </StatCard>
        </StatCardsContainer>
        
        {/* Charts Section */}
        <ChartsContainer>
          {/* Line Chart */}
          <ChartContainer>
            <ChartTitle>Daily Communication Volume</ChartTitle>
            {loading ? (
              <LoadingContainer>
                <FaSpinner />
              </LoadingContainer>
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <LineChart
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#333', 
                      border: 'none',
                      borderRadius: '4px',
                      color: '#fff'
                    }} 
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="calls" 
                    stroke="#3498db" 
                    activeDot={{ r: 8 }} 
                    name="Calls"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="sms" 
                    stroke="#e67e22" 
                    name="SMS"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartContainer>
          
          {/* Pie Chart */}
          <ChartContainer>
            <ChartTitle>Inbound vs Outbound</ChartTitle>
            {loading ? (
              <LoadingContainer>
                <FaSpinner />
              </LoadingContainer>
            ) : (
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#3498db" />
                    <Cell fill="#e74c3c" />
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#333', 
                      border: 'none',
                      borderRadius: '4px',
                      color: '#fff'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartContainer>
        </ChartsContainer>
        
        {/* Recent Activity Section */}
        <RecentActivityContainer>
          <SectionHeader>
            <h2>
              <FaHistory />
              Recent Activity
            </h2>
            <ViewAllLink href="/communications/all">
              View All <FaArrowRight />
            </ViewAllLink>
          </SectionHeader>
          
          {loading && !stats?.recent_activity ? (
            <LoadingContainer>
              <FaSpinner />
            </LoadingContainer>
          ) : (
            <ActivityList>
              {stats?.recent_activity?.length === 0 ? (
                <div>No recent activities</div>
              ) : (
                stats?.recent_activity?.map((activity, index) => {
                  const itemKey = `${activity.type}-${activity.id}`;
                  const isExpanded = expandedItems.has(itemKey);
                  return (
                    <ActivityItem key={`${itemKey}-${index}`}>
                      <ActivityItemHeader onClick={() => toggleItemExpansion(itemKey)}>
                        <ActivityIcon type={activity.type} direction={activity.direction}>
                          {activity.type === 'call' ? (
                            activity.direction === 'inbound' ? <FaPhoneVolume /> : <FaPhoneSlash />
                          ) : (
                            <FaSms />
                          )}
                        </ActivityIcon>

                        <ActivityContent>
                          <ActivityHeader>
                            <span className="activity-type">
                              {activity.direction === 'inbound' ? 'Incoming' : 'Outgoing'} {activity.type === 'call' ? 'Call' : 'SMS'}
                            </span>
                            <span className="activity-time">{formatDateTime(activity.timestamp)}</span>
                          </ActivityHeader>

                          <ActivityDetail>
                            <div>
                              {formatPhoneNumber(activity.from)} → {formatPhoneNumber(activity.to)}
                            </div>
                            {activity.type === 'call' ? (
                              <div>Duration: {formatDuration(activity.duration)}</div>
                            ) : (
                              <div className="message-preview">{activity.body}</div>
                            )}
                          </ActivityDetail>

                          <ActivityLead>
                            {activity.lead ? (
                              <a
                                href={`/leads/view/${activity.lead.id}`}
                                onClick={(e) => handleLeadClick(e, activity.lead.id)}
                              >
                                <FaUser />
                                {activity.lead.name}
                                {activity.lead.isPlaceholder && (
                                  <span className="placeholder-badge">Unknown Contact</span>
                                )}
                              </a>
                            ) : (
                              <span className="no-lead">No lead associated</span>
                            )}
                          </ActivityLead>
                        </ActivityContent>

                        <ExpandIcon>
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </ExpandIcon>
                      </ActivityItemHeader>

                      {isExpanded && (
                        <ActivityExpandedDetails>
                          {activity.type === 'call' ? (
                            <>
                              <ActivityDetailRow>
                                <label>Direction:</label>
                                <div>
                                  {activity.direction === 'inbound' ? (
                                    <><FaArrowDown /> Incoming from {formatPhoneNumber(activity.from)}</>
                                  ) : (
                                    <><FaArrowUp /> Outgoing to {formatPhoneNumber(activity.to)}</>
                                  )}
                                </div>
                              </ActivityDetailRow>
                              <ActivityDetailRow>
                                <label>Duration:</label>
                                <div>{formatDuration(activity.duration)}</div>
                              </ActivityDetailRow>
                              {activity.recording_path && (
                                <ActivityDetailRow>
                                  <label>Recording:</label>
                                  <audio controls src={`${URL_API}${activity.recording_path}`}>
                                    Your browser does not support the audio element.
                                  </audio>
                                </ActivityDetailRow>
                              )}
                              {activity.notes && (
                                <ActivityDetailRow>
                                  <label>Notes:</label>
                                  <div>{activity.notes}</div>
                                </ActivityDetailRow>
                              )}
                              {activity.recording_path && (
                                <TranscriptionSection
                                  callId={activity.id}
                                  transcription={activity.transcription}
                                  transcriptionStatus={activity.transcription_status}
                                  transcribedAt={activity.transcribed_at}
                                />
                              )}
                            </>
                          ) : (
                            <>
                              <ActivityDetailRow>
                                <label>Message:</label>
                                <ActivitySmsBody>{activity.body}</ActivitySmsBody>
                              </ActivityDetailRow>
                              {activity.media_paths && activity.media_paths.length > 0 && (
                                <ActivityDetailRow>
                                  <label>Media:</label>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px', width: '100%' }}>
                                    {activity.media_paths.map((url, idx) => (
                                      isAudioUrl(url) ? (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#272727', borderRadius: '8px', padding: '10px', borderLeft: '3px solid #F69620', width: '100%' }}>
                                          <FaVolumeUp style={{ color: '#F69620', fontSize: '1.1rem', flexShrink: 0 }} />
                                          <audio controls preload="metadata" style={{ flex: 1, minWidth: 0, height: '36px' }}>
                                            <source src={`${URL_API}${url}`} />
                                          </audio>
                                        </div>
                                      ) : (
                                        <a key={idx} href={`${URL_API}${url}`} target="_blank" rel="noopener noreferrer" style={{ width: '100px', height: '100px', overflow: 'hidden', borderRadius: '8px' }}>
                                          <img src={`${URL_API}${url}`} alt={`Media ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </a>
                                      )
                                    ))}
                                  </div>
                                </ActivityDetailRow>
                              )}
                            </>
                          )}
                          <ActivityActionButtons>
                            <ActivityActionButton onClick={() => openCallModal(
                              activity.direction === 'inbound' ? activity.from : activity.to,
                              activity.lead?.id
                            )}>
                              <FaPhone /> Call
                            </ActivityActionButton>
                            <ActivityActionButton onClick={() => openSmsComposer(
                              activity.direction === 'inbound' ? activity.from : activity.to,
                              activity.lead?.id
                            )}>
                              {activity.type === 'sms' ? <FaReply /> : <FaSms />}
                              {activity.type === 'sms' ? 'Reply' : 'Send SMS'}
                            </ActivityActionButton>
                          </ActivityActionButtons>
                        </ActivityExpandedDetails>
                      )}
                    </ActivityItem>
                  );
                })
              )}
            </ActivityList>
          )}
        </RecentActivityContainer>
        
        {/* Placeholder Leads Section */}
        {stats?.placeholder_leads?.count > 0 && (
          <PlaceholderLeadsContainer>
            <SectionHeader>
              <h2>
                <FaQuestionCircle />
                Unknown Contacts
              </h2>
              <ViewAllLink href="/communications/placeholder-leads">
                View All <FaArrowRight />
              </ViewAllLink>
            </SectionHeader>
            
            {loading ? (
              <LoadingContainer>
                <FaSpinner />
              </LoadingContainer>
            ) : (
              <PlaceholderLeadsList>
                {stats?.placeholder_leads?.top_leads?.map((lead) => (
                  <PlaceholderLeadCard 
                    key={lead.id}
                    onClick={() => handlePlaceholderLeadClick(lead.id)}
                  >
                    <PlaceholderLeadHeader>
                      <h4>
                        <FaQuestionCircle />
                        {lead.name}
                        <span className="placeholder-badge">Unknown</span>
                      </h4>
                    </PlaceholderLeadHeader>
                    
                    <PlaceholderLeadPhone>
                      {formatPhoneNumber(lead.phone)}
                    </PlaceholderLeadPhone>
                    
                    <PlaceholderLeadStats>
                      <span>
                        <FaPhone /> {lead.callCount} calls
                      </span>
                      <span>
                        <FaSms /> {lead.smsCount} messages
                      </span>
                    </PlaceholderLeadStats>
                  </PlaceholderLeadCard>
                ))}
              </PlaceholderLeadsList>
            )}
          </PlaceholderLeadsContainer>
        )}
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default CommunicationsDashboard;